import { RefObject, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { StructuredAnswer, StructuredSurveyTemplate } from '@dreryk/egabinet-survey-frontend-api';

import { useMedicalProfileFormContext } from '../MedicalProfileFormContext';
import { MedicalProfileTabFormValues, VisibilityResolver } from '../tab/MedicalProfileTab.types';
import { answerToCodes, createFormFieldName, valueToCodes } from '../tab/MedicalProfileTab.utils';
import { createVisibilityResolver, GATE_YES_CODE } from '../tab/MedicalProfileVisibility.utils';

const NOT_FOUND = -1;
const UNWATCHED_FIELD_NAME = 'medicalProfile:unwatched';

type VisibilityResolverParams = {
   readonly templateCode: string;
   readonly template: StructuredSurveyTemplate;
   readonly initialAnswers: StructuredAnswer[];
   readonly controlQuestionIds: number[];
   readonly entry?: { readonly sectionId: number; readonly key: string };
   readonly assumeYesQuestionId?: number;
};

export const useSectionScroll = (shouldScroll: boolean): RefObject<HTMLDivElement> => {
   const ref = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (shouldScroll) {
         ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
   }, [shouldScroll]);

   return ref;
};

export const useVisibilityResolver = (params: VisibilityResolverParams): VisibilityResolver => {
   const { control } = useMedicalProfileFormContext();
   const entrySectionId = params.entry?.sectionId;
   const entryKey = params.entry?.key;
   const names = useMemo(
      () =>
         params.controlQuestionIds.map((questionId) =>
            createFormFieldName(
               params.templateCode,
               questionId,
               entrySectionId === undefined || entryKey === undefined
                  ? undefined
                  : { entry: { sectionId: entrySectionId, key: entryKey } }
            )
         ),
      [params.templateCode, params.controlQuestionIds, entrySectionId, entryKey]
   );
   const watchedNames = useMemo(() => (names.length === 0 ? [UNWATCHED_FIELD_NAME] : names), [names]);
   const initialValues = useMemo(() => control.getValues(watchedNames), [control, watchedNames]);
   const values = useWatch<MedicalProfileTabFormValues>({
      name: watchedNames,
      control: control,
      defaultValue: initialValues,
   });

   const readCodes = (questionId: number): string[] => {
      const index = params.controlQuestionIds.indexOf(questionId);
      const value = index === NOT_FOUND ? undefined : values[names[index]];
      if (value !== undefined) {
         return valueToCodes(value);
      }
      if (entrySectionId !== undefined) {
         return [];
      }
      return answerToCodes(params.initialAnswers.find((answer) => answer.questionId === questionId));
   };

   const getAnswer = (questionId: number): string[] => {
      const codes = readCodes(questionId);
      return questionId === params.assumeYesQuestionId && codes.length === 0 ? [GATE_YES_CODE] : codes;
   };

   return createVisibilityResolver(params.template, getAnswer, entrySectionId);
};
